import { getShowTenant, updateTenantData } from "@/Api/tenants";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Mask } from "@/utils/mask";
import { Check, ImageUp, Loader, Pencil, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { useToastNavigate } from "@/utils/toastNavigate";

export type TypeSimpleDataForm = {
  name: string;
  document: string;
  logo: string | object | null;
  email: string;
  phone: string;
};

export const isDiferentValues = (primary: {}, comparation: {}): boolean => {
  let isDiferent = false;

  Object.keys(primary).forEach((key) => {
    if (primary[key] !== comparation[key]) isDiferent = true;
  });

  return isDiferent;
};

export const urlToFile = async (url, fileName = "archive_database") => {
  try {
    const response = await axios.get(url, { responseType: "blob" });
    if (response.status !== 200) {
      throw new Error(`Erro ao baixar o recurso: ${response.statusText}`);
    }
    const blob = response.data;

    const mimeType = blob.type;
    const extension = mimeType.split("/")[1];

    const dinamicName = `${fileName}.${extension}`;

    const file = new File([blob], dinamicName, {
      type: mimeType,
      lastModified: Date.now(),
    });

    return file;
  } catch (error) {
    console.error("Erro ao converter URL para File:", error);
    return null;
  }
};

export const UpdateTenantBtn = ({ setTenantsData, idTenant, arrKey }) => {
  const [originalData, setOriginalData] = useState({} as TypeSimpleDataForm);
  const { toast } = useToastNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm();
  const formWatch = watch();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [state, setState] = useState("fetch");

  const handleOpenUpdate = async () => {
    const { data: responseApi, ok } = await getShowTenant(idTenant);
    if (!ok) {
      setIsOpen(false);
      return console.log("error response");
    }

    const onlyNumbers = /^\d+$/;
    // setando cada input com dados e suas respectivas máscaras
    ["name", "document", "phone", "email", "logo"].forEach((valueKey) => {
      const tenantValue = responseApi?.response[valueKey];

      const valueMask =
        valueKey === "document"
          ? Mask.maskCpfOrCnpj(tenantValue) || ""
          : Mask.maskPhone(tenantValue) || "";

      if (onlyNumbers.test(tenantValue)) {
        setValue(valueKey, valueMask);
        setOriginalData((prev) => ({
          ...prev,
          [valueKey]: valueMask,
        }));
      } else {
        setValue(valueKey, tenantValue);
        setOriginalData((prev) => ({
          ...prev,
          [valueKey]: tenantValue,
        }));
      }
    });

    setState("done");
    setIsOpen(true);
  };

  const formSubmit = async (newData) => {
    setIsLoad((prev) => !prev);
    setIsEdited(false);
    const payload = {
      ...newData,
      logo:
        typeof newData?.logo === "string"
          ? await urlToFile(newData?.logo)
          : newData?.logo?.[0] || null,
    };

    const { data: apiResponse, ok } = await updateTenantData(idTenant, payload);
    if (!ok) {
      setIsLoad((prev) => !prev);
      setIsOpen(false);
      return;
    }

    setTenantsData((prev) => {
      prev?.data?.splice(arrKey, 1, apiResponse?.response);
      return {
        ...prev,
        data: [...prev.data],
      };
    });

    toast({
      description: `${apiResponse?.response?.name} atualizada com sucesso!`,
      action: <Check color="#1edc2b" />,
    });
    setIsLoad((prev) => !prev);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  useEffect(() => {
    isDiferentValues(formWatch, originalData)
      ? setIsEdited(true)
      : setIsEdited(false);
  }, [
    formWatch.name,
    formWatch.document,
    formWatch.phone,
    formWatch.email,
    formWatch.logo,
  ]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="mx-1 size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={handleOpenUpdate}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] gap-1">
        <DialogHeader className="flex flex-col gap-2">
          <DialogTitle className="text-2xl">Editar Instituição</DialogTitle>
          <DialogDescription className="text-lg">
            Informações básicas
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(formSubmit)}>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <div className="sm:col-span-3">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="username"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Nome:
                  </label>
                  {errors.name && (
                    <span className="text-sm text-red-400">
                      {errors?.name?.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("name", {
                    required: "*Obrigatório",
                  })}
                  className="mt-1"
                  placeholder={
                    state === "fetch" ? "Carregando..." : "Insira um nome"
                  }
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="sm:col-span-5">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="document"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Documento:
                  </label>
                  {errors.document && (
                    <span className="text-sm text-red-400">
                      {errors.document.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("document", {
                    required: "*Obrigatório",
                    minLength: {
                      value: 14,
                      message: "O mínimo de dígitos é 9",
                    },
                  })}
                  className="mt-1"
                  typeMask="cpfOrCnpj"
                  placeholder={
                    state === "fetch" ? "Carregando..." : "Insira os dados"
                  }
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="sm:col-span-5">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="email"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Email:
                  </label>
                  {errors.email && (
                    <span className="text-sm text-red-400">
                      {errors.email.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("email", {
                    required: "*Obrigatório",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Formato inválido.",
                    },
                  })}
                  className="mt-1"
                  placeholder={
                    state === "fetch" ? "Carregando..." : "Insira um e-mail"
                  }
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="sm:col-span-5">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="phone"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Telefone:
                  </label>
                  {errors.phone && (
                    <span className="text-sm text-red-400">
                      {errors.phone.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("phone", {
                    required: "*Obrigatório",
                    minLength: {
                      value: 15,
                      message: "O mínimo de dígitos é 9",
                    },
                  })}
                  typeMask="phone"
                  className="mt-1"
                  placeholder={
                    state === "fetch" ? "Carregando..." : "Insira um telefone"
                  }
                />
              </div>
            </div>
          </div>
          <div className="mt-5">
            <label className="text-1xl font-medium text-gray-900 dark:text-zinc-300">
              Foto:
            </label>
            <div className="flex justify-center itens-center rounded-md border border-dashed dark:border-gray-500 border-gray-900/25 px-2 py-3 mt-2">
              <div className="flex flex-col items-center gap-4">
                {formWatch?.logo && formWatch?.logo?.length > 0 ? (
                  typeof formWatch?.logo === "string" ? (
                    <img
                      className="rounded-md aspect-square w-40 object-cover"
                      src={formWatch?.logo}
                      alt="logo_company"
                    />
                  ) : (
                    // <LoadImage src={formWatch?.logo} className="rounded-md aspect-square w-40 object-cover" iconSize={50} /> :
                    <img
                      className="rounded-md aspect-square w-40 object-cover"
                      src={URL.createObjectURL(formWatch?.logo?.[0])}
                      alt="logo_company"
                    />
                  )
                ) : (
                  <ImageUp className="mx-auto size-12 text-gray-300 mt-2" />
                )}
                <div className="flex justify-center items-center gap-5 text-sm/6 text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-transparent font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <span className="flex items-center gap-1">
                      {formWatch?.logo?.length > 0 && (
                        <ImageUp className="size-5" />
                      )}
                      Procurar imagem
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      className="sr-only"
                      {...register("logo")}
                    />
                  </label>
                  {formWatch?.logo && formWatch?.logo?.length > 0 && (
                    <Button
                      className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                      onClick={() => setValue("logo", null)}
                    >
                      <Trash className="size-5" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-5">
            <Button
              type="submit"
              className={cn("flex gap-2", colors.bg_primary)}
              disabled={!isEdited}
            >
              {isLoad ? (
                <>
                  <Loader className="animate-spin size-4" />
                  Atualizando
                </>
              ) : (
                <>Atualizar</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
